import { ReactNode, useEffect } from 'react';

import * as Sentry from '@sentry/react';

import { ConfigProvider, theme as antdTheme } from 'antd';
import { ThemeProvider } from 'styled-components';
import { QueryClientProvider } from '@tanstack/react-query';
import {
  RouterProvider,
  // For Sentry Config
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { Worker } from '@react-pdf-viewer/core';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isBetween from 'dayjs/plugin/isBetween';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';

//// If Redux, to uncomment and install Libraries with:
//// ` yarn add react-redux redux-thunk @reduxjs/toolkit `
// import { Provider } from 'react-redux';
// import store from '@/store/Redux';
//// With uncommenting the files in `src\store\Redux`

//// If GraphQL - Apollo, to uncomment and install Libraries with:
//// ` yarn add @apollo/client graphql `
// import { ApolloProvider } from '@apollo/client';
// import apolloClient from '@/modules/apolloClient';
//// With uncommenting the file `src\modules\apolloClient.ts`

//// If Tailwind, to uncomment and install Libraries with:
//// ` yarn add tailwindcss postcss autoprefixer `
// import './index.css';
//// With uncommenting the files: `src\index.css`, `postcss.config.js` and `tailwind.config.js`

// For Internationalization
import '@/config/i18nextConfig';
import useInternationalization from '@/modules/internationalization';

// Theme Variables - For Design System
import '@/styles/global.css';
import appTheme from '@/styles/theme';

// For React Query
import queryClient from '@/config/reactQueryConfig';

// For App Rooting
import router from '@/config/routesConfig';

// Context Provider
import { P4RContextProvider } from '@/store/P4RContext';

// Components
import CurrentTimeGlobalStyles from '@/components/CurrentTimeGlobalStyles';

import useRolesCondition from './utils/useRolesCondition';

// extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);
dayjs.extend(customParseFormat);
dayjs.extend(duration);
dayjs.extend(localizedFormat);
dayjs.extend(weekOfYear);
dayjs.extend(quarterOfYear);

Sentry.init({
  dsn: 'https://02fd5e5dfdfddaf6118beabb34635ee4@o366774.ingest.us.sentry.io/4510107316584448',
  tunnel: '/tunnel',
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration({ useCompression: false }),
  ],
  tracePropagationTargets: [/.*\.heka\.ai*.*/],
  // Performance Monitoring
  tracesSampleRate: 1, //  Capture 100% of the transactions
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 0. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  // TODO: find a better way of setting environment & enabled
  environment: window.location.hostname.includes('.dev') ? 'dev' : 'prod',
  enabled:
    window.location.protocol === 'https:' &&
    window.location.hostname.includes('heka.ai'),
});

// For AntDesign Theme & Styles variables extent
const { useToken } = antdTheme;

function StyledComponentsThemeProvider({ children }: { children: ReactNode }) {
  const { token } = useToken();

  // Create a theme object that combines Ant Design token with custom properties
  const styledComponentsTheme = {
    ...token,
    ...appTheme.token,
  };

  return (
    <ThemeProvider theme={styledComponentsTheme}>{children}</ThemeProvider>
  );
}

// The App
function App() {
  const { language, antdLocale } = useInternationalization();

  const path = window.location.pathname;

  const hasFetchCondition = !path.includes('/login');

  const {
    controlRoomDisplay,
    onlyControlRoom,
    settingsDisplay,
    settingsPlatformTabDisplay,
    settingsUserMangementTabDisplay,
  } = useRolesCondition(hasFetchCondition);

  const MenuDisplayConditions = [
    {
      key: '/control-room',
      display: controlRoomDisplay,
    },
    {
      key: '/licence-area/district/:id',
      display: !onlyControlRoom,
    },
    {
      key: '/licence-area',
      display: !onlyControlRoom,
    },
    {
      key: '/event-library/:id',
      display: !onlyControlRoom,
    },
    {
      key: '/event-library',
      display: !onlyControlRoom,
    },
    {
      key: '/forecast-quality',
      display: !onlyControlRoom,
    },
    {
      key: '/settings',
      display: !onlyControlRoom && settingsDisplay,
    },
    {
      key: 'platform-settings',
      display: !onlyControlRoom && settingsPlatformTabDisplay,
    },
    {
      key: 'users-management',
      display: !onlyControlRoom && settingsUserMangementTabDisplay,
    },
    {
      key: '/user-preferences',
      display: !onlyControlRoom,
    },
  ];

  // Add mac-os class to body if user is using mac
  useEffect(() => {
    const isMacDevice = navigator.userAgent.includes('Macintosh');
    if (isMacDevice) {
      document.body.classList.add('mac-os');
    }
  }, []);

  return (
    <ConfigProvider
      locale={antdLocale}
      direction={language === 'ar' ? 'rtl' : 'ltr'}
      theme={{
        cssVar: true,
        ...appTheme,
        components: {
          ...appTheme.components,
          Tabs: {
            colorBorderSecondary: '#1DC4B6',
            inkBarColor: '#1DC4B6',
          },
        },
      }}
    >
      <StyledComponentsThemeProvider>
        <CurrentTimeGlobalStyles />
        {/* If Redux, to uncomment */}
        {/* <Provider store={store}> */}
        {/* If Apollo, to uncomment */}
        {/* <ApolloProvider client={apolloClient}> */}
        <RouterProvider router={router(MenuDisplayConditions)} />
        {/* </ApolloProvider> */}
        {/* </Provider> */}
      </StyledComponentsThemeProvider>
    </ConfigProvider>
  );
}

export default function AppWithContextProvider() {
  return (
    <P4RContextProvider>
      <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </Worker>
    </P4RContextProvider>
  );
}
